import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Helmet } from 'react-helmet'

import SEO, { SITE_URL } from '../../components/seo'
import Layout from '../../layouts/layout'
import ToTop from '../../layouts/totop'
import {
  TwentyTwoMotionProvider,
  useTwentyTwoMotion,
  useTwentyTwoViewport,
} from '../../components/twenty-two-motion'
import cards from './assets/data/cards.json'
import { CardFlow } from './card-flow'
import featuredLetter from './assets/data/featured-letter.json'
import brandLogo from './assets/images/logo.png'
import headerLogo from './assets/images/header-logo.png'
import favicon32 from './assets/images/favicon-32.png'
import favicon192 from './assets/images/favicon-192.png'
import appleTouchIcon from './assets/images/apple-touch-icon.png'
import envelopeHead from './assets/images/envelope-head.png'
import envelopeBeneath from './assets/images/envelope-beneath.png'
import envelopeBody from './assets/images/envelope-body.png'
import envelopeHeadWebp from './assets/images/envelope-head.webp'
import envelopeBeneathWebp from './assets/images/envelope-beneath.webp'
import envelopeBodyWebp from './assets/images/envelope-body.webp'
import './twenty-two.css'

const PAGE_PATH = '/22:06/'
const PAGE_TITLE = '二十二点零六'
const PAGE_DESCRIPTION =
  '二十二点零六（Six past Twenty Two）小程序的表白卡片存档，以瀑布流完整展示 144 封校园表白与当年的卡片细节。'

const WARM_WAVE_PATHS = [
  'm -158 228.759 c 381.046 -411 572.046 -153.25 825.046 -101 s 289 130 457.004 115 c 168 -15 987 245 987 245 h -2269.05 z',
  'm -158 228.759 c 330 -362 548 -210 825.046 -101 s 303 112 471.004 104 c 183 -9 973 255 973 255 h -2269.05 z',
  'm -158 228.759 c 285 -300 520 -275 825.046 -101 s 322 84 490.004 91 c 202 2 954 268 954 268 h -2269.05 z',
]

const COOL_WAVE_PATHS = [
  'M -240 154 C 120 70 410 126 690 202 C 980 282 1260 54 1840 158 L 1840 420 L -240 420 Z',
  'M -240 190 C 90 142 420 60 760 176 C 1040 272 1330 92 1840 130 L 1840 420 L -240 420 Z',
  'M -240 166 C 130 238 470 76 820 148 C 1130 212 1420 118 1840 184 L 1840 420 L -240 420 Z',
]

const DEEP_WAVE_PATHS = [
  'M -120 88 C 150 32 365 8 620 68 S 1080 132 1560 72 V 468 H -120 Z',
  'M -120 96 C 190 82 410 2 670 54 S 1120 148 1560 88 V 468 H -120 Z',
  'M -120 82 C 235 120 450 38 735 42 S 1175 112 1560 104 V 468 H -120 Z',
]

const waveValues = (paths) => [...paths, paths[1], paths[0]].join(';')
const WAVE_KEY_SPLINES =
  '0.45 0 0.55 1;0.45 0 0.55 1;0.45 0 0.55 1;0.45 0 0.55 1'

const WavePathAnimation = ({ paths, duration, begin }) => (
  <animate
    attributeName="d"
    begin={begin}
    calcMode="spline"
    dur={duration}
    keySplines={WAVE_KEY_SPLINES}
    keyTimes="0;0.25;0.5;0.75;1"
    repeatCount="indefinite"
    values={waveValues(paths)}
  />
)

const ELEVATOR_SEGMENTS = {
  0: ['a', 'b', 'c', 'd', 'e', 'f'],
  2: ['a', 'b', 'd', 'e', 'g'],
  6: ['a', 'c', 'd', 'e', 'f', 'g'],
}

const ELEVATOR_SEGMENT_POINTS = {
  a: '12,1 52,1 57,6 52,11 12,11 7,6',
  b: '58,14 63,19 63,43 58,48 53,43 53,19',
  c: '58,64 63,69 63,93 58,98 53,93 53,69',
  d: '12,101 52,101 57,106 52,111 12,111 7,106',
  e: '6,64 11,69 11,93 6,98 1,93 1,69',
  f: '6,14 11,19 11,43 6,48 1,43 1,19',
  g: '12,51 52,51 57,56 52,61 12,61 7,56',
}

const ELEVATOR_ZERO_SEGMENT_POINTS = {
  b: '58,14 63,19 63,47 58,52 53,47 53,19',
  c: '58,60 63,65 63,93 58,98 53,93 53,65',
  e: '6,60 11,65 11,93 6,98 1,93 1,65',
  f: '6,14 11,19 11,47 6,52 1,47 1,19',
}

const ElevatorDigit = ({ value }) => (
  <svg
    className="TwentyTwoElevatorDigit"
    viewBox="0 0 64 112"
    preserveAspectRatio="xMidYMid meet"
    focusable="false"
    aria-hidden="true"
  >
    {ELEVATOR_SEGMENTS[value].map((segment) => (
      <polygon
        className={`TwentyTwoElevatorSegment TwentyTwoElevatorSegment--${segment}`}
        key={segment}
        points={
          (value === 0 && ELEVATOR_ZERO_SEGMENT_POINTS[segment]) ||
          ELEVATOR_SEGMENT_POINTS[segment]
        }
      />
    ))}
  </svg>
)

const ElevatorColon = ({ active }) => {
  const colonActive = useSecondPulse(active)
  return (
    <span
      className={`TwentyTwoElevatorColon ${colonActive ? 'is-active' : ''}`}
      aria-hidden="true"
    >
      <i />
      <i />
    </span>
  )
}

const ElevatorTime = memo(({ active }) => (
  <span className="TwentyTwoElevatorTime" role="img" aria-label="22点06分">
    <ElevatorDigit value={2} />
    <ElevatorDigit value={2} />
    <ElevatorColon active={active} />
    <ElevatorDigit value={0} />
    <ElevatorDigit value={6} />
  </span>
))

const FlowingHeroWaves = memo(({ motionEnabled, isActive, containerRef }) => {
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // Preserve the SMIL timeline when offscreen rather than restarting the waves.
    container.querySelectorAll('svg').forEach((svg) => {
      if (isActive && motionEnabled) {
        if (svg.unpauseAnimations) svg.unpauseAnimations()
      } else if (svg.pauseAnimations) svg.pauseAnimations()
    })
  }, [isActive, motionEnabled, containerRef])

  return (
    <div className="TwentyTwoHeroWaves" aria-hidden="true" ref={containerRef}>
      <svg
        className="TwentyTwoHeroWave TwentyTwoHeroWave--warm"
        viewBox="0 0 1440 488"
        preserveAspectRatio="xMidYMid slice"
        focusable="false"
      >
        <defs>
          <linearGradient
            id="twenty-two-warm-gradient"
            gradientUnits="userSpaceOnUse"
            x1="-4.11625"
            x2="-4.11621"
            y1="-92.7575"
            y2="433.759"
          >
            <stop offset="0" stopColor="#ff6869" />
            <stop offset=".484375" stopColor="#ff927a" />
            <stop offset="1" stopColor="#ff5f8f" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={WARM_WAVE_PATHS[0]} fill="url(#twenty-two-warm-gradient)">
          {motionEnabled && (
            <WavePathAnimation
              paths={WARM_WAVE_PATHS}
              duration="16s"
              begin="0s"
            />
          )}
        </path>
      </svg>

      <svg
        className="TwentyTwoHeroWave TwentyTwoHeroWave--cool"
        viewBox="0 0 1600 420"
        preserveAspectRatio="xMidYMid slice"
        focusable="false"
      >
        <defs>
          <linearGradient
            id="twenty-two-cool-gradient"
            gradientUnits="userSpaceOnUse"
            x1="800"
            x2="800"
            y1="56"
            y2="420"
          >
            <stop offset="0" stopColor="#8790ec" stopOpacity=".78" />
            <stop offset=".55" stopColor="#b49bdd" stopOpacity=".68" />
            <stop offset="1" stopColor="#d990c2" stopOpacity=".72" />
          </linearGradient>
        </defs>
        <path d={COOL_WAVE_PATHS[0]} fill="url(#twenty-two-cool-gradient)">
          {motionEnabled && (
            <WavePathAnimation
              paths={COOL_WAVE_PATHS}
              duration="22s"
              begin="-5s"
            />
          )}
        </path>
      </svg>

      <svg
        className="TwentyTwoHeroWave TwentyTwoHeroWave--deep"
        viewBox="0 0 1440 468"
        preserveAspectRatio="xMidYMid slice"
        focusable="false"
      >
        <path d={DEEP_WAVE_PATHS[0]} fill="#1f1f47">
          {motionEnabled && (
            <WavePathAnimation
              paths={DEEP_WAVE_PATHS}
              duration="38s"
              begin="-14s"
            />
          )}
        </path>
      </svg>
    </div>
  )
})

const structuredData = {
  '@type': 'CollectionPage',
  '@id': `${SITE_URL}${PAGE_PATH}#collection`,
  url: `${SITE_URL}${PAGE_PATH}`,
  name: PAGE_TITLE,
  description: PAGE_DESCRIPTION,
  inLanguage: 'zh-CN',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: {
    '@type': 'CreativeWork',
    name: '二十二点零六（Six past Twenty Two）',
    description: '同济大学校园表白墙微信小程序',
  },
  mainEntity: {
    '@type': 'ItemList',
    name: '二十二点零六表白卡片',
    numberOfItems: cards.length,
  },
}

// The curated order is intentionally local and static. Updating cards.json will not
// silently reshuffle the wall; edit these rows when the editorial order changes.
const FIXED_CARD_ROW_IDS = [
  [
    'card-042',
    'card-135',
    'card-001',
    'card-069',
    'card-028',
    'card-121',
    'card-109',
    'card-075',
  ],
  [
    'card-101',
    'card-133',
    'card-131',
    'card-140',
    'card-081',
    'card-002',
    'card-099',
    'card-137',
  ],
  [
    'card-085',
    'card-096',
    'card-141',
    'card-004',
    'card-052',
    'card-067',
    'card-136',
    'card-092',
  ],
  [
    'card-097',
    'card-139',
    'card-134',
    'card-100',
    'card-084',
    'card-029',
    'card-003',
    'card-098',
  ],
  [
    'card-091',
    'card-008',
    'card-037',
    'card-089',
    'card-118',
    'card-057',
    'card-090',
    'card-019',
  ],
  [
    'card-021',
    'card-123',
    'card-065',
    'card-035',
    'card-005',
    'card-056',
    'card-068',
    'card-038',
  ],
  [
    'card-095',
    'card-108',
    'card-026',
    'card-033',
    'card-030',
    'card-006',
    'card-060',
    'card-083',
  ],
  [
    'card-015',
    'card-124',
    'card-007',
    'card-117',
    'card-105',
    'card-053',
    'card-114',
    'card-107',
  ],
  [
    'card-011',
    'card-063',
    'card-034',
    'card-116',
    'card-115',
    'card-103',
    'card-088',
    'card-112',
  ],
  [
    'card-119',
    'card-070',
    'card-040',
    'card-027',
    'card-014',
    'card-111',
    'card-110',
    'card-087',
  ],
  [
    'card-025',
    'card-013',
    'card-048',
    'card-080',
    'card-062',
    'card-079',
    'card-023',
    'card-059',
  ],
  [
    'card-018',
    'card-047',
    'card-064',
    'card-142',
    'card-045',
    'card-032',
    'card-073',
    'card-058',
  ],
  [
    'card-009',
    'card-072',
    'card-094',
    'card-036',
    'card-106',
    'card-074',
    'card-113',
    'card-044',
  ],
  [
    'card-138',
    'card-066',
    'card-076',
    'card-049',
    'card-039',
    'card-024',
    'card-010',
    'card-017',
  ],
  [
    'card-125',
    'card-144',
    'card-020',
    'card-016',
    'card-055',
    'card-077',
    'card-071',
    'card-127',
  ],
  [
    'card-043',
    'card-120',
    'card-051',
    'card-128',
    'card-086',
    'card-061',
    'card-046',
    'card-104',
  ],
  [
    'card-082',
    'card-050',
    'card-102',
    'card-022',
    'card-143',
    'card-122',
    'card-012',
    'card-031',
  ],
  [
    'card-054',
    'card-093',
    'card-041',
    'card-129',
    'card-130',
    'card-078',
    'card-126',
    'card-132',
  ],
]

const cardsById = new Map(cards.map((card) => [card.id, card]))
const cardRows = FIXED_CARD_ROW_IDS.map((row) =>
  row.map((cardId) => cardsById.get(cardId))
)
const fixedCardIds = FIXED_CARD_ROW_IDS.reduce(
  (allCardIds, row) => allCardIds.concat(row),
  []
)
const invalidFixedCardRows =
  cardRows.some(
    (row) =>
      row.length !== 8 ||
      row.some((card) => !card) ||
      row.filter((card) => card && card.vip).length > 1
  ) ||
  fixedCardIds.length !== cards.length ||
  new Set(fixedCardIds).size !== cards.length

if (invalidFixedCardRows) {
  throw new Error(
    '22:06 fixed card rows must contain every card once, eight per row, with at most one VIP'
  )
}

const useHeroReady = () => {
  const [heroReady, setHeroReady] = useState(false)

  useEffect(() => {
    let backgroundPaintFrame
    let contentRevealFrame

    const revealHero = () => {
      backgroundPaintFrame = window.requestAnimationFrame(() => {
        contentRevealFrame = window.requestAnimationFrame(() =>
          setHeroReady(true)
        )
      })
    }

    if (document.readyState === 'complete') {
      revealHero()
    } else {
      window.addEventListener('load', revealHero, { once: true })
    }

    return () => {
      window.removeEventListener('load', revealHero)
      if (backgroundPaintFrame)
        window.cancelAnimationFrame(backgroundPaintFrame)
      if (contentRevealFrame) window.cancelAnimationFrame(contentRevealFrame)
    }
  }, [])

  return heroReady
}

const useSecondPulse = (active) => {
  const [colonActive, setColonActive] = useState(true)

  useEffect(() => {
    if (!active) return undefined
    let timer

    const tick = () => {
      setColonActive(new Date().getSeconds() % 2 === 0)
      timer = window.setTimeout(tick, 1000 - (Date.now() % 1000) + 20)
    }

    tick()
    return () => window.clearTimeout(timer)
  }, [active])

  return colonActive
}

const useHeroParallax = (motionEnabled, heroRef) => {
  const backgroundRef = useRef(null)
  const contentRef = useRef(null)

  useEffect(() => {
    const hero = heroRef.current
    const background = backgroundRef.current
    const content = contentRef.current

    if (!motionEnabled || !hero || !background || !content) return undefined

    let pointerX = 0
    let pointerY = 0
    let animationFrame

    const handleMouseMove = (event) => {
      pointerX = event.pageX
      pointerY = event.pageY

      if (animationFrame) return

      animationFrame = window.requestAnimationFrame(() => {
        const x = (window.innerWidth - pointerX) / 100
        const y = (window.innerHeight - pointerY) / 100

        background.style.transform = `scale(1.1) translate3d(${x}px, ${y}px, 0)`
        content.style.transform = `translate3d(${-x}px, ${-y}px, 0)`
        animationFrame = undefined
      })
    }

    hero.addEventListener('mousemove', handleMouseMove, { passive: true })

    return () => {
      hero.removeEventListener('mousemove', handleMouseMove)
      if (animationFrame) window.cancelAnimationFrame(animationFrame)
      background.style.transform = ''
      content.style.transform = ''
    }
  }, [motionEnabled, heroRef])

  return { backgroundRef, contentRef }
}

const FeaturedEnvelopeLetter = memo(({ letter }) => {
  const { ref, isNearViewport } = useTwentyTwoViewport('160px 0px')
  const { documentVisible, motionEnabled } = useTwentyTwoMotion()
  const isActive = isNearViewport && documentVisible && motionEnabled
  const [letterState, setLetterState] = useState('closed')
  const envelopeButtonRef = useRef(null)
  const restoreEnvelopeFocusRef = useRef(false)
  const open = letterState === 'open'
  const closing = letterState === 'closing'
  const letterParts = useMemo(
    () =>
      letter.description
        .split(/((?:🍒){2,})/)
        .map((part) => part.trim())
        .filter(Boolean)
        .reduce((parts, part) => {
          if (part.split('🍒').join('') === '') {
            return parts.concat({ type: 'divider', content: part })
          }

          const paragraphs = part
            .split(/(?=🌙🌙(?:在图书馆|「))/)
            .map((paragraph) => paragraph.trim())
            .filter(Boolean)

          return parts.concat(
            paragraphs.map((content) => ({ type: 'paragraph', content }))
          )
        }, []),
    [letter.description]
  )

  useEffect(() => {
    let transitionTimer
    let focusFrame

    if (letterState === 'closing') {
      transitionTimer = window.setTimeout(() => setLetterState('closed'), 1050)
    } else if (letterState === 'closed' && restoreEnvelopeFocusRef.current) {
      restoreEnvelopeFocusRef.current = false
      focusFrame = window.requestAnimationFrame(() => {
        if (envelopeButtonRef.current) envelopeButtonRef.current.focus()
      })
    }

    return () => {
      if (transitionTimer) window.clearTimeout(transitionTimer)
      if (focusFrame) window.cancelAnimationFrame(focusFrame)
    }
  }, [letterState])

  const closeLetter = () => {
    if (!open) return

    restoreEnvelopeFocusRef.current = true
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setLetterState('closed')
    } else {
      setLetterState('closing')
    }
  }

  const toggleLetter = () => {
    if (letterState === 'closed') {
      setLetterState('open')
    } else if (open) {
      closeLetter()
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault()
      closeLetter()
    }
  }

  return (
    <section
      ref={ref}
      className={`TwentyTwoFeaturedLetter is-${letterState} ${
        isActive ? 'is-active' : ''
      }`}
      aria-label="二十二点零六写给同济的一封信"
      onKeyDown={handleKeyDown}
    >
      <div className="TwentyTwoFeaturedLetterStage">
        <article
          className="TwentyTwoFeaturedLetterSheet"
          id="twenty-two-featured-letter-content"
          aria-hidden={!open}
        >
          <button
            className="TwentyTwoFeaturedLetterClose"
            type="button"
            aria-label="收起信件"
            tabIndex={open ? '0' : '-1'}
            onClick={closeLetter}
          >
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <path d="m5.2 7.4 4.8 4.8 4.8-4.8" />
            </svg>
          </button>

          <header className="TwentyTwoFeaturedLetterHeader">
            <div className="TwentyTwoFeaturedLetterPeople">
              <span>{letter.nameLeft}</span>
              <span className="TwentyTwoHeart" aria-hidden="true">
                ♥
              </span>
              <span>{letter.nameRight}</span>
            </div>
            <div className="TwentyTwoCardTags TwentyTwoFeaturedLetterTags">
              <span>{letter.academy}</span>
              <span>{letter.grade}</span>
            </div>
          </header>

          <div
            className="TwentyTwoFeaturedLetterBubbles"
            aria-label="写信人与收信人的话"
          >
            <blockquote>{letter.bubbleLeft}</blockquote>
            <blockquote>{letter.bubbleRight}</blockquote>
          </div>

          <div
            className="TwentyTwoFeaturedLetterScroll"
            tabIndex={open ? '0' : '-1'}
          >
            {letterParts.map((part, index) =>
              part.type === 'divider' ? (
                <div
                  className="TwentyTwoFeaturedLetterDivider"
                  key={`featured-letter-divider-${index}`}
                  aria-hidden="true"
                >
                  {part.content}
                </div>
              ) : (
                <p key={`featured-letter-paragraph-${index}`}>{part.content}</p>
              )
            )}
            <footer className="TwentyTwoFeaturedLetterSignature">
              <span>祝有情人终成眷属</span>
              <span>—— 二十二点零六</span>
            </footer>
          </div>
        </article>

        <button
          className="TwentyTwoEnvelope"
          type="button"
          ref={envelopeButtonRef}
          aria-controls="twenty-two-featured-letter-content"
          aria-expanded={open}
          aria-disabled={closing}
          aria-label={
            open
              ? '收起二十二点零六写给同济的信'
              : '展开二十二点零六写给同济的信'
          }
          onClick={toggleLetter}
        >
          <picture>
            <source srcSet={envelopeBeneathWebp} type="image/webp" />
            <img
              className="TwentyTwoEnvelopeBack"
              src={envelopeBeneath}
              width="627"
              height="370"
              loading="lazy"
              decoding="async"
              alt=""
              draggable="false"
              aria-hidden="true"
            />
          </picture>
          <picture>
            <source srcSet={envelopeBodyWebp} type="image/webp" />
            <img
              className="TwentyTwoEnvelopeBody"
              src={envelopeBody}
              width="627"
              height="383"
              loading="lazy"
              decoding="async"
              alt=""
              draggable="false"
              aria-hidden="true"
            />
          </picture>
          <picture>
            <source srcSet={envelopeHeadWebp} type="image/webp" />
            <img
              className="TwentyTwoEnvelopeFlap"
              src={envelopeHead}
              width="625"
              height="273"
              loading="lazy"
              decoding="async"
              alt=""
              draggable="false"
              aria-hidden="true"
            />
          </picture>
          <span className="TwentyTwoEnvelopeAddress" aria-hidden="true">
            <span>{letter.nameLeft}</span>
            <span className="TwentyTwoHeart">♥</span>
            <span>{letter.nameRight}</span>
          </span>
          <span className="TwentyTwoEnvelopeSeal" aria-hidden="true">
            <img
              className="TwentyTwoEnvelopeSealLogo"
              src={favicon192}
              width="192"
              height="192"
              loading="lazy"
              decoding="async"
              alt=""
              draggable="false"
            />
          </span>
        </button>
      </div>
    </section>
  )
})

const TwentyTwoHero = memo(() => {
  const { ref: heroRef, isNearViewport } = useTwentyTwoViewport()
  const { documentVisible, motionEnabled, loopEnabled } = useTwentyTwoMotion()
  const isActive = isNearViewport && documentVisible && motionEnabled
  const heroReady = useHeroReady()
  const { backgroundRef, contentRef } = useHeroParallax(
    isActive && loopEnabled,
    heroRef
  )
  return (
    <header
      className={`TwentyTwoHero ${isActive ? 'is-active' : ''}`}
      ref={heroRef}
    >
      <FlowingHeroWaves
        motionEnabled={loopEnabled}
        isActive={isActive}
        containerRef={backgroundRef}
      />
      <div
        className={`TwentyTwoHeroInner ${heroReady ? 'is-ready' : ''}`}
        ref={contentRef}
      >
        <h1 className="TwentyTwoElevatorTitle">
          <ElevatorTime active={isActive && heroReady} />
        </h1>
        <p className="TwentyTwoHeroTagline">每个晚上都会遇见</p>
        <p className="TwentyTwoLead">
          我曾在同济校园里做过一个只在每晚22:06开放16分钟的微信小程序
          <br />
          如今，我将这些心动卡片在此重新悬挂
        </p>
      </div>
    </header>
  )
})

const TwentyTwoPage = ({ location }) => {
  const [expandedCard, setExpandedCard] = useState(null)
  const toggleCard = useCallback((cardId) => {
    setExpandedCard((current) => (current === cardId ? null : cardId))
  }, [])
  return (
    <Layout
      location={location}
      hideSocialMedias
      navigationLogo={headerLogo}
      navigationLogoAlt="二十二点零六"
      navigationLogoClassName="TwentyTwoNavigationLogo"
      footerInstitution="Tongji University"
      footerLeadText="没有更多表白了，这里是有底线的～"
    >
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        pathname={PAGE_PATH}
        image={brandLogo}
        imageAlt="二十二点零六项目标志"
        includeDefaultKeywords={false}
        author="doubleZ"
        keywords={[
          '二十二点零六',
          'Six past Twenty Two',
          '同济大学表白墙',
          '校园表白卡片',
          '微信小程序作品',
        ]}
        structuredData={structuredData}
      />
      <Helmet
        title={PAGE_TITLE}
        bodyAttributes={{ class: 'TwentyTwoDocument' }}
      >
        <meta name="darkreader-lock" />
        <link rel="icon" type="image/png" sizes="32x32" href={favicon32} />
        <link rel="icon" type="image/png" sizes="192x192" href={favicon192} />
        <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon} />
      </Helmet>
      <ToTop />

      <main className="TwentyTwoPage">
        <TwentyTwoHero />

        <section
          className="TwentyTwoArchive"
          aria-labelledby="twenty-two-archive-title"
        >
          <div className="TwentyTwoArchiveHeading">
            <h2 id="twenty-two-archive-title">二十二点零六 · 心动存档</h2>
          </div>

          <FeaturedEnvelopeLetter letter={featuredLetter} />

          <div className="TwentyTwoFlows">
            {cardRows.map((row, rowIndex) => {
              const flowDuration = Math.round(
                row.length * 5.2 + (rowIndex % 4) * 3
              )
              const rowExpandedCard = row.some(
                (card) => card.id === expandedCard
              )
                ? expandedCard
                : null

              return (
                <CardFlow
                  key={`flow-seamless-${rowIndex}`}
                  cards={row}
                  expandedCard={rowExpandedCard}
                  flowDuration={flowDuration}
                  rowIndex={rowIndex}
                  onToggle={toggleCard}
                />
              )
            })}
          </div>
        </section>
      </main>
    </Layout>
  )
}

export default function TwentyTwoPageWithMotion(props) {
  return (
    <TwentyTwoMotionProvider>
      <TwentyTwoPage {...props} />
    </TwentyTwoMotionProvider>
  )
}
